/**
 * Feature flag for gradual REST API rollout.
 *
 * Gates migration-dependent REST endpoints behind the backend database
 * migration status. Supports a localStorage flag for local testing.
 */

/**
 * localStorage key for database migration status.
 * Set to 'true' when backend database migrations are complete.
 *
 * Usage in TEST environment:
 *   localStorage.setItem('PRESIZE_DB_MIGRATIONS_COMPLETE', 'true');
 */
const DB_MIGRATIONS_KEY = 'PRESIZE_DB_MIGRATIONS_COMPLETE';

/**
 * Checks if database migrations are complete.
 *
 * Controls whether migration-dependent REST endpoints can be used:
 * - POST /api/orders/{order_code}/article-groups
 * - PATCH /api/orders/{order_code}/articles
 * - GET /api/recommended-size-charts*
 *
 * Returns true if localStorage flag 'PRESIZE_DB_MIGRATIONS_COMPLETE' === 'true'
 *
 * Migration requirements:
 * 1. Make article.group_id NULLABLE
 * 2. Set default FALSE for article.is_reviewed column
 * 3. Enable dual-write in Python backend for article updates
 * 4. Backfill article data from article_group table
 *
 * When backend migrations complete:
 * 1. Test in TEST environment: localStorage.setItem('PRESIZE_DB_MIGRATIONS_COMPLETE', 'true')
 * 2. Verify all REST endpoints work
 * 3. Deploy with flag enabled by default (or set in environment config)
 *
 * @returns true if migrations complete and REST should be used for all endpoints
 *
 * @example
 * // Hybrid flow - orders via REST, groups via GraphQL (until migrations)
 * const order = await presizeBackendRest.getOrder(orderId);  // Always REST
 *
 * if (isDbMigrated()) {
 *   // Use REST for groups (after migrations)
 *   const groups = await presizeBackendRest.createArticleGroups(orderId, articles);
 * } else {
 *   // Use GraphQL for groups (until migrations)
 *   const result = await presizeBackend.updateOrder(orderId, ...);
 *   const groups = result.groups;
 * }
 *
 * @example
 * // Enable in TEST environment after migrations verified:
 * localStorage.setItem('PRESIZE_DB_MIGRATIONS_COMPLETE', 'true');
 *
 * @example
 * // Disable (default):
 * localStorage.removeItem('PRESIZE_DB_MIGRATIONS_COMPLETE');
 */
export function isDbMigrated(): boolean {
  try {
    const migrationsComplete = localStorage.getItem(DB_MIGRATIONS_KEY);
    const useRest = migrationsComplete === 'true';

    return useRest;
  } catch (err) {
    // localStorage may not be available in some environments
    // Conservative: assume migrations not complete if we can't check
    return false;
  }
}
